import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Wand2, Facebook, Instagram, CalendarDays } from "lucide-react";
import { api, imgUrl, fileUrl } from "@/lib/api";

export const inp = "bg-black/30 border border-white/15 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C77746]";
export const btn = "inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-[11px] uppercase tracking-widest hover:border-white/50 disabled:opacity-40";
export const gold = "inline-flex items-center gap-2 px-4 py-2 bg-[#C77746] text-black text-[11px] uppercase tracking-widest disabled:opacity-40";
export const ChannelIcon = ({ c }) => (c === "facebook" ? <Facebook size={12} /> : <Instagram size={12} />);
export const fmtWhen = (iso, tz) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "unscheduled");

export function PostImages({ post, size = "w-20 h-20" }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {post.images.map((im, i) => (
        <div key={im.id} className={`${size} relative bg-black/40 overflow-hidden`} title={im.title}>
          {im.path && <img src={imgUrl(fileUrl(im.path), 320)} alt={im.title} className="w-full h-full object-cover" />}
          <span className="absolute top-1 left-1 bg-black/70 text-[10px] px-1">{i + 1}</span>
        </div>
      ))}
    </div>
  );
}

function PostCard({ post, tz, onChanged }) {
  const [caption, setCaption] = useState(post.caption);
  const [busy, setBusy] = useState(false);
  useEffect(() => setCaption(post.caption), [post.caption, post.id]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const pickVariant = (v) => act(() => api.patch(`/admin/social/posts/${post.id}`, { selected_variant: v.id }));
  const saveCaption = () => caption !== post.caption && act(() => api.patch(`/admin/social/posts/${post.id}`, { caption }), "Caption saved");
  return (
    <div className="border border-white/10 bg-white/[0.03] p-5" data-testid={`queue-post-${post.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-widest text-white/50 mb-4">
        <span className="inline-flex items-center gap-1 text-white"><ChannelIcon c={post.channel} /> {post.channel}</span>
        <span>· {post.theme_name}</span>
        <span className={post.audience === "business" ? "text-sky-300" : "text-amber-300"}>· {post.audience}</span>
        <span className="inline-flex items-center gap-1">· <CalendarDays size={11} /> {fmtWhen(post.scheduled_at, tz)}</span>
        {post.channel === "instagram" && <span>· {post.image_format}</span>}
      </div>
      <div className="grid md:grid-cols-[1fr_260px] gap-6">
        <div>
          <div className="flex gap-2 mb-3 flex-wrap" data-testid={`variants-${post.id}`}>
            {post.variants.map((v, i) => (
              <button key={v.id} type="button" onClick={() => pickVariant(v)} disabled={busy}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border ${post.selected_variant === v.id ? "border-[#C77746] text-[#C77746]" : "border-white/15 text-white/50 hover:border-white/40"}`}
                data-testid={`variant-${post.id}-${i}`}>{String.fromCharCode(65 + i)} · {v.hook_style}</button>
            ))}
          </div>
          <textarea value={caption} onChange={e => setCaption(e.target.value)} onBlur={saveCaption} rows={7} className={`${inp} w-full leading-relaxed`} data-testid={`caption-${post.id}`} />
          {post.channel === "instagram" && post.hashtags.length > 0 && <div className="text-xs text-sky-300/80 mt-2 leading-relaxed" data-testid={`hashtags-${post.id}`}>{post.hashtags.join(" ")}</div>}
          {post.link_url && <div className="text-xs text-white/40 mt-2 truncate" data-testid={`link-${post.id}`}>→ {post.link_url}</div>}
          {post.reel_concept && (
            <details className="mt-3 text-xs text-white/60"><summary className="cursor-pointer uppercase tracking-widest text-[11px] text-white/40">Reel concept</summary>
              <pre className="whitespace-pre-wrap font-sans mt-2 leading-relaxed">{post.reel_concept}</pre></details>
          )}
        </div>
        <div>
          <div className="overline text-white/40 mb-2">Images ({post.images.length})</div>
          <PostImages post={post} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-white/10">
        <button type="button" className={gold} disabled={busy} onClick={() => act(() => api.post(`/admin/social/posts/${post.id}/approve`), "Approved")} data-testid={`approve-${post.id}`}><Check size={12} /> Approve</button>
        <button type="button" className={`${btn} hover:border-red-400 hover:text-red-400`} disabled={busy} onClick={() => act(() => api.post(`/admin/social/posts/${post.id}/reject`), "Rejected")} data-testid={`reject-${post.id}`}><X size={12} /> Reject</button>
        <button type="button" className={btn} disabled={busy} onClick={() => act(() => api.post(`/admin/social/posts/${post.id}/regenerate`), "Regenerated")} data-testid={`regenerate-${post.id}`}><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Regenerate</button>
      </div>
    </div>
  );
}

function GenerateBar({ themes, onDone }) {
  const [f, setF] = useState({ theme_id: "", channel: "facebook", count: 1, brief: "", image_format: "carousel" });
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (!f.theme_id && themes[0]) setF(p => ({ ...p, theme_id: themes[0].id })); }, [themes, f.theme_id]);
  const run = async (week) => {
    setBusy(week ? "week" : "one");
    try {
      const r = week ? await api.post("/admin/social/generate-week") : await api.post("/admin/social/generate", { ...f, count: Number(f.count) });
      toast.success(week ? `Drafted ${r.data.created} posts for ${r.data.week}` : `${r.data.length} draft${r.data.length > 1 ? "s" : ""} added to the queue`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(null); }
  };
  return (
    <div className="border border-white/10 p-4 grid md:grid-cols-[1fr_140px_80px_130px_1fr_auto_auto] gap-2 items-end" data-testid="generate-bar">
      <label className="text-xs text-white/60">Theme<select value={f.theme_id} onChange={e => setF({ ...f, theme_id: e.target.value })} className={`${inp} w-full mt-1`} data-testid="gen-theme">{themes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
      <label className="text-xs text-white/60">Channel<select value={f.channel} onChange={e => setF({ ...f, channel: e.target.value })} className={`${inp} w-full mt-1`} data-testid="gen-channel"><option value="facebook">Facebook</option><option value="instagram">Instagram</option></select></label>
      <label className="text-xs text-white/60">Posts<input type="number" min="1" max="7" value={f.count} onChange={e => setF({ ...f, count: e.target.value })} className={`${inp} w-full mt-1`} data-testid="gen-count" /></label>
      <label className="text-xs text-white/60">IG format<select value={f.image_format} onChange={e => setF({ ...f, image_format: e.target.value })} className={`${inp} w-full mt-1`} data-testid="gen-format"><option value="carousel">Carousel</option><option value="single">Single</option></select></label>
      <label className="text-xs text-white/60">Brief (optional)<input value={f.brief} onChange={e => setF({ ...f, brief: e.target.value })} placeholder="e.g. spring gifts, mention free shipping…" className={`${inp} w-full mt-1`} data-testid="gen-brief" /></label>
      <button type="button" className={gold} disabled={!!busy || !f.theme_id} onClick={() => run(false)} data-testid="gen-run"><Wand2 size={12} className={busy === "one" ? "animate-pulse" : ""} /> {busy === "one" ? "Writing…" : "Generate"}</button>
      <button type="button" className={btn} disabled={!!busy} onClick={() => run(true)} data-testid="gen-week"><CalendarDays size={12} /> {busy === "week" ? "Drafting week…" : "Draft next week (7)"}</button>
    </div>
  );
}

export function Queue({ themes, settings, reloadKey }) {
  const [posts, setPosts] = useState([]);
  const load = () => api.get("/admin/social/posts?status=draft").then(r => setPosts(r.data));
  useEffect(() => { load(); }, [reloadKey]);
  return (
    <div className="space-y-6" data-testid="social-queue">
      <GenerateBar themes={themes} onDone={load} />
      <div className="overline text-white/40">Approval queue · {posts.length} awaiting review · nothing posts without your approval</div>
      {posts.length === 0 && <p className="text-white/40 text-sm" data-testid="queue-empty">Queue is empty — generate a post above or ask the chat panel.</p>}
      {posts.map(p => <PostCard key={p.id} post={p} tz={settings.timezone} onChanged={load} />)}
    </div>
  );
}
